import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import DashAction from './DashAction';
import { formatItemAmount, formatObjectFx, getObjectFx, rateTitle } from '../model';
import type { AnalyticsDashMetric } from '../model';

interface DashPriceProps {
    isUahMode: boolean;
    price: number;
    priceRateAmount?: number;
    priceRow?: AnalyticsDashMetric | null;
    actualCostLabel?: string;
    actualCostAmount?: number;
    actualCostDisabled?: boolean;
}

const DashPrice = ({
    isUahMode,
    price,
    priceRateAmount,
    priceRow,
    actualCostLabel,
    actualCostAmount,
    actualCostDisabled = false,
}: DashPriceProps) => {
    const { t } = useTranslation(['Objects']);
    const objectFx = isUahMode ? null : getObjectFx(price, priceRateAmount);
    const showActualCost = actualCostLabel != null && (actualCostDisabled || actualCostAmount != null);
    const actualCostMismatch =
        showActualCost &&
        !actualCostDisabled &&
        Math.round(Number(actualCostAmount) * 100) !== Math.round(Number(price) * 100);

    if (!priceRow && !showActualCost) return null;

    return (
        <div className="object-analytics-dash__price">
            {priceRow ? (
                <div className="object-analytics-dash__price-line">
                    <span className="object-analytics-dash__tile-label object-analytics-dash__label-with-icon text-muted">
                        {priceRow.icon ? <SolarIcon name={priceRow.icon} aria-hidden="true" /> : null}
                        {priceRow.label}
                        {priceRow.actionLabel && priceRow.onAction ? (
                            <DashAction label={priceRow.actionLabel} onClick={priceRow.onAction} />
                        ) : null}
                    </span>
                    <span className="object-analytics-dash__price-values">
                        <span
                            className="object-analytics-dash__price-amount text-body-emphasis fw-bold"
                            title={rateTitle(isUahMode, priceRow.rateTitle)}
                        >
                            {formatItemAmount(priceRow.amount, isUahMode)}
                        </span>
                        {objectFx != null ? (
                            <span className="object-analytics-dash__price-rate text-muted">
                                {withLocalCurrency(formatPrice(Number(priceRateAmount) || 0))}
                                {' · '}
                                {t('infoItems.objectRate', { rate: formatObjectFx(objectFx) })}
                            </span>
                        ) : null}
                    </span>
                </div>
            ) : null}
            {showActualCost ? (
                <div className={`object-analytics-dash__actual${actualCostDisabled ? ' is-disabled' : ''}`}>
                    <span className="text-muted">{actualCostLabel}</span>
                    {actualCostDisabled ? (
                        <span className="text-muted">—</span>
                    ) : (
                        <span className={actualCostMismatch ? 'text-danger fw-medium' : 'text-body fw-medium'}>
                            {formatItemAmount(Number(actualCostAmount), isUahMode)}
                        </span>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default DashPrice;
